import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { admin } from "@/lib/api";
import { Field, TabHeader, Badge, inputCls, textareaCls, slugify } from "@/admin/ui";

const ICON_OPTIONS = ["HeartHandshake", "Sunrise", "School", "Video", "Library", "GraduationCap", "FileText", "ClipboardCheck", "Sparkles", "Users"];

const EMPTY = {
  title: "", slug: "", icon: "HeartHandshake", tagline: "", short: "",
  overviewText: "", benefitsText: "", whoForText: "", faqsText: "",
  href: "", published: true, order: 99,
};

const parasToText = (arr) => (arr || []).join("\n\n");
const textToParas = (t) => t.split(/\n\s*\n+/).map((s) => s.trim()).filter(Boolean);
const linesToText = (arr) => (arr || []).join("\n");
const textToLines = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);
const faqsToText = (faqs) => (faqs || []).map((f) => `${f.q} | ${f.a}`).join("\n");
const textToFaqs = (t) =>
  t.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [q, ...rest] = l.split("|");
    return { q: (q || "").trim(), a: rest.join("|").trim() };
  }).filter((f) => f.q && f.a);

const toForm = (s) => ({
  ...EMPTY, ...s,
  overviewText: parasToText(s.overview),
  benefitsText: linesToText(s.benefits),
  whoForText: linesToText(s.whoFor),
  faqsText: faqsToText(s.faqs),
});

export default function ServicesTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => admin.list("services").then(setItems).catch(() => toast.error("Could not load services"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
        overview: textToParas(form.overviewText),
        benefits: textToLines(form.benefitsText),
        whoFor: textToLines(form.whoForText),
        faqs: textToFaqs(form.faqsText),
        href: form.href || null,
        order: Number(form.order) || 99,
      };
      delete payload.overviewText;
      delete payload.benefitsText;
      delete payload.whoForText;
      delete payload.faqsText;
      if (form.id) await admin.update("services", form.id, payload);
      else await admin.create("services", payload);
      toast.success("Service saved");
      setForm(null);
      load();
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? Its detail page will go offline.`)) return;
    await admin.remove("services", s.id);
    toast.success("Service deleted");
    load();
  };

  if (form) {
    return (
      <div data-testid="admin-service-form">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-foreground">{form.id ? "Edit Service" : "New Service"}</h1>
          <button onClick={() => setForm(null)} className="btn-secondary !px-4 !py-2 !text-xs"><X className="h-3.5 w-3.5" /> Cancel</button>
        </div>
        <div className="mt-8 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft sm:grid-cols-2">
          <Field label="Title"><input className={inputCls} value={form.title} onChange={set("title")} data-testid="service-title-input" /></Field>
          <Field label="Slug" hint="Leave blank to auto-generate."><input className={inputCls} value={form.slug} onChange={set("slug")} data-testid="service-slug-input" /></Field>
          <Field label="Icon">
            <select className={inputCls} value={form.icon} onChange={set("icon")} data-testid="service-icon-select">
              {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Tagline" hint="Small uppercase line under the title."><input className={inputCls} value={form.tagline} onChange={set("tagline")} data-testid="service-tagline-input" /></Field>
          <div className="sm:col-span-2">
            <Field label="Short description" hint="Shown on the service cards."><textarea className={textareaCls} rows={2} value={form.short} onChange={set("short")} data-testid="service-short-input" /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Overview" hint="Separate paragraphs with a blank line."><textarea className={textareaCls} rows={5} value={form.overviewText} onChange={set("overviewText")} data-testid="service-overview-input" /></Field>
          </div>
          <Field label="Benefits" hint="One per line."><textarea className={textareaCls} rows={5} value={form.benefitsText} onChange={set("benefitsText")} data-testid="service-benefits-input" /></Field>
          <Field label="Who it's for" hint="One per line."><textarea className={textareaCls} rows={5} value={form.whoForText} onChange={set("whoForText")} data-testid="service-whofor-input" /></Field>
          <div className="sm:col-span-2">
            <Field label="FAQs" hint="One per line, formatted as: Question | Answer"><textarea className={textareaCls} rows={4} value={form.faqsText} onChange={set("faqsText")} data-testid="service-faqs-input" /></Field>
          </div>
          <Field label="Custom link" hint="Optional — send the card to another page (e.g. /resources) instead of a detail page.">
            <input className={inputCls} value={form.href || ""} onChange={set("href")} data-testid="service-href-input" placeholder="/resources" />
          </Field>
          <Field label="Order" hint="Lower numbers appear first."><input type="number" className={inputCls} value={form.order} onChange={set("order")} data-testid="service-order-input" /></Field>
          <Field label="Visibility">
            <label className="flex h-11 items-center gap-2.5 text-sm text-foreground">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="service-published-checkbox" className="h-4 w-4 accent-terracotta-500" />
              Visible on the website
            </label>
          </Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-service-save" className="btn-primary mt-6 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save Service"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-services-tab">
      <TabHeader
        title="Services"
        description="Edit existing services or add future offerings — new services appear on the site automatically."
        actionLabel="New Service"
        onAdd={() => setForm({ ...EMPTY })}
        testId="admin-add-service"
      />
      <div className="mt-8 space-y-3">
        {(items || []).map((s) => (
          <div key={s.id} data-testid={`admin-service-row-${s.slug}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{s.title}</p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.short}</p>
            </div>
            {!s.published && <Badge>hidden</Badge>}
            <button onClick={() => setForm(toForm(s))} data-testid={`admin-service-edit-${s.slug}`} aria-label={`Edit ${s.title}`} className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(s)} data-testid={`admin-service-delete-${s.slug}`} aria-label={`Delete ${s.title}`} className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
